/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Checkbox, CircularProgress, Divider, InputAdornment, TextField } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { useSetAtom } from 'jotai';
import { QSTypography } from '../../../../../../../shared/components/atoms';
import { useFilterFieldValues } from '../hooks/use-filter-field-values';
import { customFieldSelectLabelsAtom } from '../state';
import { CustomFieldConditionValue, SelectValueLabel } from '../typings';
import SearchIcon from '../../../../../../../shared/icons/icons-revamp/search';

interface Props {
  fieldId: string;
  fieldName: string;
  value: CustomFieldConditionValue;
  singleSelectMode: boolean;
  onChange: (next: CustomFieldConditionValue, knownLabels: SelectValueLabel[]) => void;
}

const SelectValueInput = ({ fieldId, fieldName, value, singleSelectMode, onChange }: Props) => {
  const { items, loading, loadingMore, error, hasMore, search, setSearch, fetchMore } = useFilterFieldValues(fieldId);
  const setSelectLabels = useSetAtom(customFieldSelectLabelsAtom);

  useEffect(() => {
    if (items.length === 0) return;
    setSelectLabels((prev) => {
      const existing = prev[fieldId] ?? [];
      const known = new Set(existing.map((l) => l.value));
      const additions = items.filter((l) => !known.has(l.value));
      if (additions.length === 0) return prev;
      return { ...prev, [fieldId]: [...existing, ...additions] };
    });
  }, [items, fieldId, setSelectLabels]);

  const selectedIds = useMemo(() => {
    if (value.type !== 'VALUE') return [] as string[];
    const raw = value.value;
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string' && raw.length > 0) return [raw];
    return [];
  }, [value]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = useCallback(
    (item: SelectValueLabel) => {
      let nextIds: string[];
      if (singleSelectMode) {
        nextIds = selectedSet.has(item.value) ? [] : [item.value];
      } else if (selectedSet.has(item.value)) {
        nextIds = selectedIds.filter((id) => id !== item.value);
      } else {
        nextIds = [...selectedIds, item.value];
      }
      onChange({ type: 'VALUE', value: nextIds }, [item]);
    },
    [onChange, selectedIds, selectedSet, singleSelectMode]
  );

  const handleEndReached = useCallback(() => {
    if (hasMore && !loadingMore && !loading) {
      fetchMore();
    }
  }, [hasMore, loadingMore, loading, fetchMore]);

  const renderItem = useCallback(
    (_: number, item: SelectValueLabel) => (
      <Box
        onClick={() => toggle(item)}
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 4px;
          cursor: pointer;
          border-radius: 4px;
          &:hover {
            background: #f7f7f7;
          }
        `}
      >
        <Checkbox
          size="small"
          checked={selectedSet.has(item.value)}
          sx={{ padding: '4px', color: '#c4c4c4', '&.Mui-checked': { color: '#3f906d' } }}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(item)}
        />
        <QSTypography
          css={css`
            font-size: 14px;
            color: #2d2d2d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            min-width: 0;
          `}
        >
          {item.label}
        </QSTypography>
      </Box>
    ),
    [toggle, selectedSet]
  );

  const renderFooter = useCallback(() => {
    if (!loadingMore) return null;
    return (
      <Box
        css={css`
          display: flex;
          justify-content: center;
          padding: 8px;
        `}
      >
        <CircularProgress size={14} sx={{ color: '#7a7a7a' }} />
      </Box>
    );
  }, [loadingMore]);

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 0;
        flex: 1;
      `}
    >
      <Divider sx={{ m: 0 }} />
      <TextField
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search values`}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon size={16} color="#7a7a7a" />
            </InputAdornment>
          ),
          sx: {
            fontSize: 14,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
          },
        }}
      />
      <Divider sx={{ mb: 1 }} />
      <Box
        css={css`
          height: 220px;
          display: flex;
          flex-direction: column;
        `}
      >
        {loading && (
          <Box
            css={css`
              display: flex;
              justify-content: center;
              padding: 16px;
            `}
          >
            <CircularProgress size={18} sx={{ color: '#3f906d' }} />
          </Box>
        )}
        {error && !loading && (
          <QSTypography
            css={css`
              padding: 12px;
              color: #b00020;
              font-size: 13px;
              text-align: center;
            `}
          >
            Could not load values
          </QSTypography>
        )}
        {!loading && !error && items.length === 0 && (
          <Box
            css={css`
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
            `}
          >
            <QSTypography
              css={css`
                color: #7a7a7a;
                font-size: 13px;
                text-align: center;
              `}
            >
              No values found
            </QSTypography>
          </Box>
        )}
        {!loading && !error && items.length > 0 && (
          <Virtuoso
            data={items}
            totalCount={items.length}
            endReached={handleEndReached}
            increaseViewportBy={200}
            itemContent={renderItem}
            components={{ Footer: renderFooter }}
            computeItemKey={(_, item) => item.value}
            style={{ height: '100%' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(SelectValueInput);
